import { useState } from 'react';
import { Eye, Layers, Download, RefreshCw, Wrench, ArrowLeft } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { MockupPreview } from './MockupPreview';
import { LayerOutput } from './LayerOutput';

export function Review() {
  const config = useProjectStore((state) => state.config);
  const exportProject = useProjectStore((state) => state.exportProject);
  const setActiveTab = useProjectStore((state) => state.setActiveTab);
  const setUiMode = useProjectStore((state) => state.setUiMode);

  const [activeView, setActiveView] = useState<'mockup' | 'layers'>('mockup');

  const handleRegenerate = () => {
    setActiveTab('start');
  };

  const handleAdvancedEdit = () => {
    setUiMode('expert');
  };

  if (!config.designSpec) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 mb-4">
          <Layers size={32} />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          尚無網站方案
        </h2>
        <p className="text-slate-600 dark:text-slate-300 mb-6">
          請先填寫資料並生成網站方案，完成後即可在此預覽與匯出。
        </p>
        <button
          type="button"
          onClick={() => setActiveTab('start')}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors"
        >
          <ArrowLeft size={18} />
          返回開始
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col -m-4 md:-m-6 p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">審視您的網站方案</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            預覽網站外觀並查看 Layer Prompts，滿意後即可匯出。
          </p>
        </div>

        <div className="flex p-1 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={() => setActiveView('mockup')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeView === 'mockup'
                ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <Eye size={16} />
            預覽網站
          </button>
          <button
            type="button"
            onClick={() => setActiveView('layers')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeView === 'layers'
                ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <Layers size={16} />
            Layer Prompts
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0">
        {activeView === 'mockup' ? <MockupPreview /> : <LayerOutput />}
      </div>

      <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
        <div className="text-sm text-slate-500 dark:text-slate-400">
          滿意這個方案？匯出 ZIP 給 AI 執行，或切換到進階模式微調。
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={handleRegenerate}
            className="flex items-center justify-center gap-1.5 px-4 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <RefreshCw size={14} />
            重新生成
          </button>
          <button
            type="button"
            onClick={handleAdvancedEdit}
            className="flex items-center justify-center gap-1.5 px-4 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <Wrench size={14} />
            進階編輯
          </button>
          <button
            type="button"
            onClick={exportProject}
            className="flex items-center justify-center gap-1.5 px-4 py-2 text-sm rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors"
          >
            <Download size={14} />
            匯出 ZIP
          </button>
        </div>
      </div>
    </div>
  );
}
